import {
  ActionIcon,
  Box,
  Button,
  Card,
  Group,
  Pagination,
  ScrollArea,
  SegmentedControl,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import {
  IconChevronLeft,
  IconChevronRight,
  IconCoin,
  IconFileTypePdf,
  IconSearch,
  IconSelector,
  IconShoppingCart,
  IconSortAscending,
  IconSortDescending,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { getDailySales } from "@/api/revenue";
import { getBrand } from "@/api/tenant";
import { ListError } from "@/components/DataList";
import { StatCard } from "@/components/StatCard";
import { SyncButton } from "@/components/SyncButton";
import { useFitViewport } from "@/hooks/useFitViewport";
import {
  formatPeriodRange,
  formatPrice,
  formatQuantity,
  PER_PAGE_OPTIONS,
  SALES_PERIODS,
  toIsoDay,
} from "@/lib/format";
import { notify } from "@/lib/notify";
import { downloadSalesPdf } from "@/lib/salesPdf";

const ALL_BRANCHES = "all";

const EMPTY = [];
const COLLATOR = new Intl.Collator("tr");

// Tıklanabilir sütun başlığı. Ok ikonu YALNIZCA aktif sütunda yön gösteriyor, diğerlerinde
// nötr bir işaret duruyor: hangi sütuna göre sıralandığı ve tıklanabilir oldukları bir arada
// okunuyor. Buton olarak veriliyor ki klavyeyle de gezilebilsin.
function SortableTh({ field, sort, onSort, align = "left", width, children }) {
  const active = sort.key === field;
  const Icon = active
    ? sort.direction === "asc"
      ? IconSortAscending
      : IconSortDescending
    : IconSelector;

  return (
    <Table.Th ta={align} w={width}>
      <UnstyledButton
        onClick={() =>
          onSort((current) =>
            current.key === field
              ? {
                  key: field,
                  direction: current.direction === "asc" ? "desc" : "asc",
                }
              : // Yeni sütuna geçerken metinde A-Z, sayıda çoktan aza: her ikisinde de
                // insanın ilk beklediği yön bu.
                {
                  key: field,
                  direction:
                    field === "name" || field === "category" ? "asc" : "desc",
                },
          )
        }
        w="100%"
      >
        <Group
          gap={4}
          justify={align === "right" ? "flex-end" : "flex-start"}
          wrap="nowrap"
        >
          <Text size="sm" fw={600} c={active ? undefined : "dimmed"}>
            {children}
          </Text>
          <Icon size={14} stroke={1.8} opacity={active ? 1 : 0.5} />
        </Group>
      </UnstyledButton>
    </Table.Th>
  );
}

// Dönem seçimi, ileri/geri okları ve şube filtresi Ciro sekmesindeki kalıbın aynısı: iki sekme
// aynı soruyu soruyor, kullanıcının ikinci bir düzen öğrenmesi için sebep yok. Aralığın ne
// anlama geldiğini de sunucu söylüyor (dailySalesRange) — "hafta" burada başka bir şey olmasın.
export function ProductSales({
  branches = [],
  sync,
  cooldown = 0,
  cooldownTotal = 0,
}) {
  // Varsayılan BUGÜN: ekranın sorusu "bugün ne sattık"; aya bakmak isteyen bir tık uzakta.
  const [period, setPeriod] = useState("today");
  const [offset, setOffset] = useState(0);
  const [customRange, setCustomRange] = useState([null, null]);
  const [branchId, setBranchId] = useState(ALL_BRANCHES);
  const [search, setSearch] = useState("");
  // Varsayılan sıralama ADET: ekranın sorduğu şey "neyi çok satıyoruz". Tutar sütunu da
  // tıklanabilir, ciro tarafına bakan biri oradan sıralıyor.
  const [sort, setSort] = useState({ key: "quantity", direction: "desc" });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [exporting, setExporting] = useState(false);
  const { data: brand } = useQuery({ queryKey: ["brand"], queryFn: getBrand });
  // Yükseklik tam satıra yuvarlanıyor: alt kenarda yarım satır kalmasın.
  const fit = useFitViewport({
    rowSelector: "tbody tr",
    headerSelector: "thead",
  });

  // Mantine takvimi değeri METİN olarak veriyor; `toIsoDay` hem metni hem Date'i kabul ediyor.
  // Kendi dönüştürücümü yazdığımda `date.getTime is not a function` ile patlamıştı.
  const customFrom = toIsoDay(customRange[0]);
  const customTo = toIsoDay(customRange[1]);
  const ready = period !== "custom" || Boolean(customFrom && customTo);

  const query = useQuery({
    queryKey: ["daily-sales", period, offset, customFrom, customTo, branchId],
    enabled: ready,
    queryFn: () =>
      getDailySales({
        period,
        offset,
        from: customFrom,
        to: customTo,
        branchId: branchId === ALL_BRANCHES ? null : Number(branchId),
      }),
    // Ciro sekmesindeki davranış: dönem değişince ekran boşalmıyor, eski veri yenisi gelene
    // kadar duruyor. Olmadığında her tıklamada iskelet çakıyor ve karşılaştırma yapmak
    // imkânsızlaşıyordu.
    placeholderData: (previous) => previous,
  });

  // Ciro toplamı SUNUCUDAN geliyor; panel gün gün toplamak için tüm kırılımı indirmek
  // zorunda değil. `EMPTY` sabit referans: her render'da yeni bir `[]` üretmek, bu diziye
  // bağlı memo'ların hiç ezberlememesi demekti.
  const products = query.data?.products ?? EMPTY;
  const totals = {
    revenue: (query.data?.revenueKurus ?? 0) / 100,
    quantity: products.reduce((sum, item) => sum + item.quantity, 0),
  };

  // Sıralama ve arama AYRI memo'larda: arama sırayı bozmuyor, yani her tuş vuruşunda bütün
  // listeyi yeniden sıralamak boşa işti. Karşılaştırıcı da bir kez kuruluyor —
  // `localeCompare` her çağrıda yeni bir collator üretiyor ve bin satırda bu görünür hale
  // geliyor.
  const sorted = useMemo(() => {
    const factor = sort.direction === "asc" ? 1 : -1;

    return [...products].sort((a, b) =>
      sort.key === "name" || sort.key === "category"
        ? factor *
          COLLATOR.compare(String(a[sort.key] ?? ""), String(b[sort.key] ?? ""))
        : factor * ((a[sort.key] ?? 0) - (b[sort.key] ?? 0)),
    );
  }, [products, sort]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase("tr");

    if (!needle) {
      return sorted;
    }

    return sorted.filter(
      (item) =>
        item.name.toLocaleLowerCase("tr").includes(needle) ||
        (item.category ?? "").toLocaleLowerCase("tr").includes(needle),
    );
  }, [sorted, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageItems = filtered.slice((page - 1) * perPage, page * perPage);

  // Arama daraltınca ya da sayfa boyu değişince kullanıcı var olmayan bir sayfada kalabiliyor:
  // liste boş görünüyor ama veri duruyordu.
  useEffect(() => {
    setPage(1);
  }, [search, perPage, period, offset, branchId]);

  const branchOptions = [
    { value: ALL_BRANCHES, label: "Tüm Şubeler" },
    ...branches.map((branch) => ({
      value: String(branch.id),
      label: branch.name,
    })),
  ];

  // Aralık seçilmemişken sorgu kapalı ve `isPending` hiç bitmiyor: iskeleti de o hâlde
  // göstermek, sonsuza kadar yükleniyor izlenimi verirdi.
  const loading = ready && query.isPending;
  // Ciro sekmesiyle aynı kural: elimizdeki en eski günden geriye sayfalamak boş dönemlerde
  // gezdirmekten başka işe yaramıyor.
  const canGoBack = Boolean(
    query.data?.earliest &&
    query.data?.range &&
    query.data.range.from > query.data.earliest,
  );

  const canExport =
    ready &&
    !loading &&
    !query.isError &&
    !query.isPlaceholderData &&
    Boolean(query.data?.range) &&
    filtered.length > 0;

  const exportPdf = async () => {
    setExporting(true);

    try {
      await downloadSalesPdf({
        brandName: brand?.name ?? "Noxus",
        branchName:
          branchId === ALL_BRANCHES
            ? null
            : (branches.find((branch) => String(branch.id) === branchId)
                ?.name ?? null),
        range: query.data.range,
        search: search.trim(),
        rows: filtered,
        totals: {
          revenue: totals.revenue,
          quantity: totals.quantity,
          products: products.length,
        },
      });
    } catch {
      notify.error({
        title: "PDF oluşturulamadı",
        message: "Bir sorun çıktı, tekrar deneyin.",
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between" gap="sm" wrap="wrap">
        {period === "custom" ? (
          <DatePickerInput
            aria-label="Tarih aralığı"
            type="range"
            value={customRange}
            onChange={setCustomRange}
            placeholder="Tarih aralığı seçin"
            valueFormat="D MMM YYYY"
            size="sm"
            w={280}
            clearable
          />
        ) : (
          <Group gap={4} wrap="nowrap">
            <ActionIcon
              variant="default"
              size="lg"
              onClick={() => setOffset((value) => value - 1)}
              disabled={!canGoBack}
              title="Önceki dönem"
            >
              <IconChevronLeft size={18} />
            </ActionIcon>
            <Text fw={600} ta="center" w={170}>
              {formatPeriodRange(period, query.data?.range)}
            </Text>
            <ActionIcon
              variant="default"
              size="lg"
              onClick={() => setOffset((value) => Math.min(0, value + 1))}
              disabled={offset === 0}
              title="Sonraki dönem"
            >
              <IconChevronRight size={18} />
            </ActionIcon>
          </Group>
        )}

        <Group gap="sm" wrap="wrap">
          <SegmentedControl
            value={period}
            onChange={(value) => {
              setPeriod(value);
              setOffset(0);
            }}
            data={SALES_PERIODS}
            size="sm"
          />
          {branches.length > 1 && (
            <Select
              aria-label="Şubeye göre filtrele"
              value={branchId}
              onChange={(value) => setBranchId(value ?? ALL_BRANCHES)}
              data={branchOptions}
              allowDeselect={false}
              w={190}
              size="sm"
            />
          )}
          <SyncButton
            mutation={sync}
            cooldown={cooldown}
            total={cooldownTotal}
          />
        </Group>
      </Group>

      {query.isError && (
        <ListError
          title="Satış verisi alınamadı"
          onRetry={() => query.refetch()}
        >
          Sunucu yanıt vermedi — bu boş bir liste değil, veriler yüklenemedi.
        </ListError>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
        <StatCard
          label="Satış tutarı"
          value={loading ? undefined : formatPrice(totals.revenue)}
          icon={IconCoin}
          color="chocolate"
        />
        <StatCard
          label="Satılan adet"
          value={loading ? undefined : formatQuantity(totals.quantity)}
          icon={IconShoppingCart}
          color="chocolate"
          hint={products.length ? `${products.length} farklı ürün` : undefined}
        />
      </SimpleGrid>

      <Card withBorder radius="md" p="md">
        <Group mb="md" gap="sm" wrap="nowrap">
          <Box style={{ flex: 1 }} visibleFrom="sm" />
          <TextInput
            w={280}
            placeholder="Ürün veya kategori ara"
            leftSection={<IconSearch size={15} />}
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
          <Group style={{ flex: 1 }} justify="flex-end">
            <Button
              variant="default"
              leftSection={<IconFileTypePdf size={16} />}
              onClick={exportPdf}
              loading={exporting}
              disabled={!canExport}
            >
              PDF indir
            </Button>
          </Group>
        </Group>

        <Card withBorder radius="md" p={0}>
          {/* Kutu ekranın alt kenarında bitiyor: arama ve sayfalama sabit kalıyor,
                        yalnızca satırlar kayıyor. */}
          <ScrollArea.Autosize
            ref={fit.ref}
            mah={fit.maxHeight}
            // Çubuk varsayılanda yalnız imleç üstündeyken beliriyor; kesilen satır
            // kaydırılabilir değil, bozuk görünüyordu.
            type="auto"
            offsetScrollbars="y"
          >
            <Table stickyHeader highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <SortableTh field="name" sort={sort} onSort={setSort}>
                    Ürün
                  </SortableTh>
                  <SortableTh field="category" sort={sort} onSort={setSort}>
                    Kategori
                  </SortableTh>
                  <SortableTh
                    field="quantity"
                    sort={sort}
                    onSort={setSort}
                    align="right"
                  >
                    Adet
                  </SortableTh>
                  <SortableTh
                    field="amountKurus"
                    sort={sort}
                    onSort={setSort}
                    align="right"
                    width={190}
                  >
                    Tutar
                  </SortableTh>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {loading ? (
                  // İskelet, dönen çarktan iyi: tablonun kaç satır geleceğini önden
                  // gösteriyor ve veri düşünce sayfa zıplamıyor.
                  Array.from({ length: 6 }, (_, index) => (
                    <Table.Tr key={index}>
                      <Table.Td colSpan={4}>
                        <Skeleton height={18} radius="sm" />
                      </Table.Td>
                    </Table.Tr>
                  ))
                ) : pageItems.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={4}>
                      <Stack gap={4} py="xl" align="center">
                        <Text fw={500}>Bu dönemde kayıt yok</Text>
                        <Text size="sm" c="dimmed" ta="center" maw={420}>
                          {
                            "Satışlar POS senkronuyla saat başı düşüyor. Yeni bağlanan bir şubede ilk tur beklenir."
                          }
                        </Text>
                      </Stack>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  pageItems.map((item) => (
                    <Table.Tr key={item.name}>
                      <Table.Td fw={500}>{item.name}</Table.Td>
                      <Table.Td c="dimmed" fz="sm">
                        {item.category ?? "—"}
                      </Table.Td>
                      <Table.Td ta="right">
                        {formatQuantity(item.quantity)}
                      </Table.Td>
                      <Table.Td ta="right" fw={500}>
                        {formatPrice(item.amountKurus / 100)}
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>
        </Card>

        <Group justify="space-between" mt="md" gap="sm" wrap="wrap">
          <Group gap="xs">
            <Text size="sm" c="dimmed">
              Sayfa başı
            </Text>
            <Select
              withCheckIcon={false}
              w={75}
              size="xs"
              data={PER_PAGE_OPTIONS}
              value={String(perPage)}
              onChange={(value) => setPerPage(Number(value))}
            />
            <Text size="sm" c="dimmed">
              {filtered.length} ürün
            </Text>
          </Group>
          <Pagination
            withEdges
            total={totalPages}
            value={page}
            onChange={setPage}
            size="sm"
          />
        </Group>
      </Card>
    </Stack>
  );
}
